import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  IconButton,
  Autocomplete,
  MenuItem,
  Alert,
  alpha,
  useTheme,
  CircularProgress,
  Chip,
  InputAdornment,
  ListItemText,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { useTranslation } from "react-i18next";
import { useUsers } from "../../../hooks/useUsers";
import { getInitials } from "../../../utils/utils";
import { isWorkspaceBoard } from "../utils/teamBoardPermissions";
import { tPlural } from "../utils/tPlural";

const ROLE_META = {
  owner: { labelKey: "boardMemberRoleOwner", hintKey: "boardMemberRoleOwnerHint" },
  admin: { labelKey: "boardMemberRoleAdmin", hintKey: "boardMemberRoleAdminHint" },
  member: { labelKey: "boardMemberRoleMember", hintKey: "boardMemberRoleMemberHint" },
  viewer: { labelKey: "boardMemberRoleViewer", hintKey: "boardMemberRoleViewerHint" },
};

function MemberAvatar({ member, size = 40 }) {
  const name = member.userName || member.participantId;
  return (
    <Avatar
      src={member.userAvatarUrl || undefined}
      sx={{ width: size, height: size, fontSize: size > 36 ? "0.85rem" : "0.75rem", fontWeight: 700 }}
    >
      {getInitials(name)}
    </Avatar>
  );
}

function renderRoleMenuItems(roles, t, disableRole) {
  return roles.map((role) => (
    <MenuItem key={role} value={role} disabled={disableRole?.(role)} sx={{ alignItems: "flex-start", py: 1.1 }}>
      <ListItemText
        primary={t(ROLE_META[role].labelKey)}
        secondary={t(ROLE_META[role].hintKey)}
        primaryTypographyProps={{ fontWeight: 700, variant: "body2" }}
        secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal" } }}
      />
    </MenuItem>
  ));
}

const roleSelectSx = {
  minWidth: { xs: "100%", sm: 188 },
  maxWidth: { sm: 220 },
  "& .MuiSelect-select": {
    py: 1,
    fontWeight: 600,
    fontSize: "0.8125rem",
  },
};

const roleMenuProps = {
  PaperProps: {
    sx: { minWidth: 300, maxWidth: 360 },
  },
};

const BoardMembersDialog = ({
  open,
  onClose,
  board,
  members = [],
  currentUserId,
  currentUserRole = null,
  canManageMembers = false,
  onAddMember,
  onRemoveMember,
  onUpdateMemberRole,
  isBusy = false,
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [addRole, setAddRole] = useState("member");
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const { data: usersData } = useUsers(1, 200, "", "humanos");
  const users = usersData?.users || [];
  const isOwner = currentUserRole === "owner";
  const assignableRoles = isOwner ? ["viewer", "member", "admin", "owner"] : ["viewer", "member"];

  const memberUserIds = useMemo(
    () => new Set((members || []).map((m) => String(m.participantId || m.userId))),
    [members]
  );

  const addableUsers = useMemo(
    () => users.filter((u) => u?.id && !memberUserIds.has(String(u.id))),
    [users, memberUserIds]
  );

  const sortedMembers = useMemo(() => {
    const list = [...(members || [])];
    list.sort((a, b) => {
      const aSelf = String(a.participantId || a.userId) === String(currentUserId);
      const bSelf = String(b.participantId || b.userId) === String(currentUserId);
      if (aSelf !== bSelf) return aSelf ? -1 : 1;
      const rank = { owner: 0, admin: 1, member: 2, viewer: 3 };
      const rankDiff = (rank[a.role] ?? 9) - (rank[b.role] ?? 9);
      if (rankDiff !== 0) return rankDiff;
      return String(a.userName || "").localeCompare(String(b.userName || ""), "pt-BR");
    });
    return list;
  }, [members, currentUserId]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedMembers;
    return sortedMembers.filter((m) => {
      const name = (m.userName || m.participantId || "").toLowerCase();
      return name.includes(q);
    });
  }, [sortedMembers, search]);

  const busy = isBusy || adding || Boolean(removingId) || Boolean(updatingId);
  const workspaceBoard = isWorkspaceBoard(board);

  useEffect(() => {
    if (open) return;
    setSearch("");
    setSelectedUser(null);
    setAddRole("member");
  }, [open]);

  const handleAdd = async () => {
    if (!selectedUser?.id || adding) return;
    setAdding(true);
    try {
      const result = await onAddMember?.({ participantId: selectedUser.id, role: addRole });
      if (result !== null && result !== false) setSelectedUser(null);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (member) => {
    if (removingId) return;
    setRemovingId(member.id);
    try {
      await onRemoveMember?.(member.id);
    } finally {
      setRemovingId(null);
    }
  };

  const handleRoleChange = async (member, role) => {
    if (!role || role === member.role || updatingId) return;
    setUpdatingId(member.id);
    try {
      await onUpdateMemberRole?.(member.id, role);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: "min(760px, calc(100% - 48px))" },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, pr: 6, pb: 1.25 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            flexShrink: 0,
            mt: 0.15,
          }}
        >
          <GroupOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
            {t("boardMembersTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
            {board?.name
              ? t("boardMembersTitleWithBoard", { name: board.name })
              : t("boardMembersTitleHint")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={busy}
          aria-label={t("boardMembersClose")}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        {!workspaceBoard ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {board?.spaceId ? t("boardMembersSpaceHint") : t("boardMembersLeadHint")}
          </Alert>
        ) : null}

        {canManageMembers && workspaceBoard ? (
          <Box
            sx={{
              mb: 2.25,
              p: 1.75,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.04),
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.35 }}>
              {t("boardMembersAddSection")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5, lineHeight: 1.45 }}>
              {t("boardMemberRoleHint")}
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              options={addableUsers}
              value={selectedUser}
              onChange={(_, value) => setSelectedUser(value)}
              disabled={busy}
              getOptionLabel={(u) => u.nome || u.name || String(u.id)}
              noOptionsText={t("boardMembersAddEmpty")}
              renderInput={(params) => (
                <TextField {...params} label={t("boardMembersAddPlaceholder")} placeholder={t("boardMembersAddSearch")} />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Avatar src={option.imagem} sx={{ width: 32, height: 32, fontSize: "0.75rem" }}>
                    {getInitials(option.nome || option.name)}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600}>
                    {option.nome || option.name}
                  </Typography>
                </Box>
              )}
              sx={{ mb: 1.25 }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "stretch" }}>
              <TextField
                select
                size="small"
                label={t("boardMembersAddRole")}
                value={assignableRoles.includes(addRole) ? addRole : "member"}
                onChange={(e) => setAddRole(e.target.value)}
                disabled={busy}
                SelectProps={{ MenuProps: roleMenuProps }}
                sx={{ ...roleSelectSx, flex: 1, maxWidth: "none" }}
              >
                {renderRoleMenuItems(assignableRoles, t)}
              </TextField>
              <Button
                variant="contained"
                disabled={!selectedUser || busy}
                onClick={handleAdd}
                startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <PersonAddOutlinedIcon />}
                sx={{ flexShrink: 0, whiteSpace: "nowrap", minHeight: 40, px: 2, fontWeight: 700 }}
              >
                {adding ? t("addingMember") : t("boardMembersAdd")}
              </Button>
            </Stack>
          </Box>
        ) : null}

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {tPlural(t, "boardMembersListSection", members.length)}
          </Typography>
        </Stack>

        {members.length > 3 ? (
          <TextField
            fullWidth
            size="small"
            placeholder={t("boardMembersSearch")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={busy}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        ) : null}

        {filteredMembers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
            {search.trim() ? t("boardMembersEmptySearch") : t("boardMembersEmpty")}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {filteredMembers.map((member) => {
              const isSelf = String(member.participantId || member.userId) === String(currentUserId);
              const roleKey = ROLE_META[member.role]?.labelKey || ROLE_META.member.labelKey;
              const ownerCount = members.filter((m) => m.role === "owner").length;
              const isSoleOwner = member.role === "owner" && ownerCount <= 1;
              const canRemove = workspaceBoard && !isSoleOwner && (isSelf || canManageMembers);
              const isRemoving = removingId === member.id;
              const canEditRole =
                canManageMembers &&
                workspaceBoard &&
                (isOwner || (member.role !== "owner" && member.role !== "admin"));
              const roleOptions = isOwner
                ? ["viewer", "member", "admin", "owner"]
                : ["viewer", "member"];

              return (
                <Box
                  key={member.id}
                  sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1.25, sm: 1.5 },
                    py: 1.25,
                    px: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, isSelf ? 0.9 : 0.7)}`,
                    bgcolor: isSelf
                      ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.06)
                      : alpha(theme.palette.action.hover, 0.35),
                    opacity: isRemoving ? 0.6 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                    <MemberAvatar member={member} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: "100%" }}>
                          {member.userName || t("commentUnknownAuthor")}
                        </Typography>
                        {isSelf ? (
                          <Chip
                            size="small"
                            label={t("boardMemberYou")}
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              bgcolor: alpha(theme.palette.primary.main, 0.14),
                              color: "primary.main",
                            }}
                          />
                        ) : null}
                      </Stack>
                      {!canEditRole ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>
                          {t(roleKey)}
                          {isSelf ? ` · ${t("boardMembersYourAccess")}` : ""}
                        </Typography>
                      ) : isSelf ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>
                          {t("boardMembersYourAccess")}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ width: { xs: "100%", sm: "auto" }, pl: { xs: 6.25, sm: 0 } }}
                  >
                    {canEditRole ? (
                      <TextField
                        select
                        size="small"
                        value={roleOptions.includes(member.role) ? member.role : "member"}
                        onChange={(e) => handleRoleChange(member, e.target.value)}
                        disabled={busy}
                        SelectProps={{ MenuProps: roleMenuProps }}
                        sx={{ ...roleSelectSx, flex: { xs: 1, sm: "none" } }}
                      >
                        {renderRoleMenuItems(roleOptions, t, (role) => isSoleOwner && role !== "owner")}
                      </TextField>
                    ) : null}
                    {canRemove ? (
                      <Button
                        size="small"
                        color={isSelf ? "inherit" : "error"}
                        onClick={() => handleRemove(member)}
                        disabled={busy}
                        startIcon={
                          isRemoving ? (
                            <CircularProgress size={14} />
                          ) : isSelf ? (
                            <LogoutOutlinedIcon fontSize="small" />
                          ) : (
                            <PersonRemoveOutlinedIcon fontSize="small" />
                          )
                        }
                        sx={{
                          flexShrink: 0,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          minWidth: "auto",
                          color: isSelf ? "text.secondary" : undefined,
                        }}
                      >
                        {isRemoving
                          ? t("removingMember")
                          : isSelf
                            ? t("boardMembersLeave")
                            : t("boardMembersRemove")}
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} variant="contained" sx={{ fontWeight: 700 }}>
          {t("boardMembersClose")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardMembersDialog;
